// src/pages/ClientDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    Button,
    Divider,
    Chip,
    CircularProgress,
    Alert,
    Stack,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    useTheme
} from '@mui/material';
import {
    NetworkCheck as NetworkIcon,
    CheckCircle as CheckIcon,
    Warning as WarningIcon,
    Receipt as ReceiptIcon,
    Support as SupportIcon,
    Speed as SpeedIcon,
    Event as CalendarIcon,  // Cambiado Calendar por Event que sí existe
    Wifi as WiFiIcon,
    ChevronLeft as PrevIcon,
    ChevronRight as NextIcon,
    Edit as EditIcon,
    MonetizationOn as MonetizationOnIcon,
    Tv as StreamingIcon,
    CardGiftcard as GiftIcon
} from '@mui/icons-material';
import { useClientAuth } from '../contexts/ClientAuthContext';
import { Link } from 'react-router-dom';
import { get, post } from '../services/apiService';
import BillingAlerts from '../components/billing/BillingAlerts';
import { usePWA } from '../contexts/PWAContext';
import PWAInstallBanner from '../components/PWAInstallBanner';
import FiberLoader from '../components/common/FiberLoader';

const parseLocalDate = (dateVal) => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return dateVal;
  const dateStr = String(dateVal);
  const cleanStr = dateStr.split('T')[0].split(' ')[0];
  const parts = cleanStr.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateVal);
};


const StatusCard = ({ title, value, icon: Icon, color, loading, bg }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    
    return (
        <Paper 
            elevation={2} 
            sx={{ 
                p: { xs: 2, md: 3 }, 
                height: '100%', 
                bgcolor: bg ? (isDark ? 'rgba(255,255,255,0.05)' : bg) : 'background.paper',
                border: bg ? '1px solid' : 'none',
                borderColor: bg ? `${color}.light` : 'none',
                boxShadow: bg ? 'none' : (isDark ? 'none' : '0 4px 20px rgba(0,0,0,0.05)'),
                borderRadius: 3
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography color="text.secondary" gutterBottom sx={{ fontSize: '0.85rem', fontWeight: 'medium' }}>
                        {title}
                    </Typography>
                    {loading ? (
                        <CircularProgress size={24} />
                    ) : (
                        <Typography 
                            variant="h6" 
                            component="div" 
                            sx={{ 
                                fontWeight: 'bold', 
                                color: bg ? (isDark ? `${color}.light` : `${color}.dark`) : 'text.primary',
                                fontSize: { xs: '1.15rem', md: '1.25rem' } 
                            }}
                        >
                            {value}
                        </Typography>
                    )}
                </Box>
                <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: bg ? (isDark ? 'rgba(255,255,255,0.1)' : '#ffffff') : (isDark ? 'rgba(255,255,255,0.08)' : `${color}.100`)
                }}>
                    <Icon sx={{ color: `${color}.main` }} fontSize="medium" />
                </Box>
            </Box>
        </Paper>
    );
};

// Resto del código exactamente igual que antes...

const ClientDashboard = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const { client, services, switchActiveService, updateServiceAlias } = useClientAuth();
    const { deferredPrompt } = usePWA();
    const [dashboardData, setDashboardData] = useState(null);
    const [trafficData, setTrafficData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pingLoading, setPingLoading] = useState(false);
    const [pingResult, setPingResult] = useState(null);

    const handlePingTest = async () => {
        setPingLoading(true);
        setPingResult(null);
        try {
            const result = await post('/api/client/dashboard/ping', {}, true);
            setPingResult(result);
        } catch (err) {
            console.error('Error al realizar prueba de ping:', err);
            setPingResult({
                success: false,
                online: false,
                message: err.message || 'No se pudo verificar la conexión en este momento.'
            });
        } finally {
            setPingLoading(false);
        }
    };
    const [activeCardIndex, setActiveCardIndex] = useState(0);
    const [switchingId, setSwitchingId] = useState(null);
    const [touchStart, setTouchStart] = useState(null);

    // Estados para el diálogo de edición de alias
    const [editAliasOpen, setEditAliasOpen] = useState(false);
    const [selectedServiceForAlias, setSelectedServiceForAlias] = useState(null);
    const [newAliasValue, setNewAliasValue] = useState('');
    const [savingAlias, setSavingAlias] = useState(false);

    const handleOpenEditAliasDialog = (service) => {
        setSelectedServiceForAlias(service);
        setNewAliasValue(service.alias || '');
        setEditAliasOpen(true);
    };

    const handleCloseEditAliasDialog = () => {
        setEditAliasOpen(false);
        setSelectedServiceForAlias(null);
        setNewAliasValue('');
    };

    const handleSaveAlias = async () => {
        if (!selectedServiceForAlias) return;
        setSavingAlias(true);
        try {
            const res = await updateServiceAlias(selectedServiceForAlias.id, newAliasValue.trim());
            if (res.success) {
                handleCloseEditAliasDialog();
                fetchDashboardData();
            } else {
                alert(res.error || 'Error al guardar el nombre personalizado');
            }
        } catch (err) {
            console.error('Error saving service alias:', err);
            alert('Ocurrió un error inesperado');
        } finally {
            setSavingAlias(false);
        }
    };
    const [touchEnd, setTouchEnd] = useState(null);

    const minSwipeDistance = 50;

    // Mantener sincronizado el índice con el servicio activo
    useEffect(() => {
        if (services && client) {
            const activeIndex = services.findIndex(srv => srv.id === client.id);
            if (activeIndex !== -1) {
                setActiveCardIndex(activeIndex);
            }
        }
    }, [client, services]);

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        
        if (isLeftSwipe && activeCardIndex < services.length - 1) {
            scrollToSlide(activeCardIndex + 1);
        } else if (isRightSwipe && activeCardIndex > 0) {
            scrollToSlide(activeCardIndex - 1);
        }
    };

    const scrollToSlide = async (index) => {
        if (index >= 0 && index < (services?.length || 0)) {
            setActiveCardIndex(index);
            const targetService = services[index];
            if (targetService && targetService.id !== client?.id) {
                setSwitchingId(targetService.id);
                try {
                    await switchActiveService(targetService.id);
                } catch (err) {
                    console.error('Error switching service auto:', err);
                } finally {
                    setSwitchingId(null);
                }
            }
        }
    };

    const handlePrevSlide = () => {
        if (activeCardIndex > 0) {
            scrollToSlide(activeCardIndex - 1);
        }
    };

    const handleNextSlide = () => {
        if (activeCardIndex < (services?.length || 0) - 1) {
            scrollToSlide(activeCardIndex + 1);
        }
    };

    // Función para formatear unidades de velocidad de manera consistente
    const formatRate = (rate) => {
        if (!rate) return '0 Kbps';

        // Si es una cadena como "10M" o "1.5k"
        if (typeof rate === 'string') {
            const match = rate.match(/^([\d.]+)([KMGkmg])?$/i);
            if (match) {
                const [, value, unit = ''] = match;
                return `${value} ${unit.toUpperCase() || 'K'}bps`;
            }
            return rate;
        }

        // Si es un número (en Kbps)
        if (rate >= 1024) {
            return `${(rate / 1024).toFixed(1)} Mbps`;
        }
        return `${rate.toFixed(1)} Kbps`;
    };

    // Función para formatear bytes
    const formatBytes = (bytes, decimals = 2) => {
        if (!bytes || bytes === '0') return '0 B';

        let value = bytes;
        if (typeof bytes === 'string') {
            // Extraer el valor numérico si es una cadena con unidades
            const match = bytes.match(/^([\d.]+)([KMGkmg])?$/i);
            if (match) {
                const [, numValue, unit = ''] = match;
                value = parseFloat(numValue);

                // Convertir según la unidad
                switch (unit.toUpperCase()) {
                    case 'K': value = value * 1024; break;
                    case 'M': value = value * 1024 * 1024; break;
                    case 'G': value = value * 1024 * 1024 * 1024; break;
                }
            } else {
                value = parseFloat(bytes);
            }
        }

        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

        if (value === 0) return '0 B';

        const i = Math.floor(Math.log(value) / Math.log(k));

        return `${parseFloat((value / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    // Extraer valor numérico de tasa
    const extractRateValue = (rateStr) => {
        if (!rateStr) return 0;

        // Para cadenas como "10M" o "1.5k"
        const match = String(rateStr).match(/^([\d.]+)([KkMmGg])?$/);
        if (!match) return 0;

        const [, value, unit = ''] = match;
        const numValue = parseFloat(value);

        // Convertir a Kbps según unidades
        switch (unit.toUpperCase()) {
            case 'K': return numValue;              // Kbps
            case 'M': return numValue * 1024;       // Mbps -> Kbps
            case 'G': return numValue * 1024 * 1024; // Gbps -> Kbps
            default: return numValue;               // Sin unidad, asumir Kbps
        }
    };

    // Formatear fecha para mostrar
    const formatDate = (dateString) => {
        if (!dateString) return 'No definida';
        const date = parseLocalDate(dateString);
        return date.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const fetchDashboardData = async () => {
        try {
            // Utilizar la función get del servicio API para mayor coherencia
            const data = await get('/api/client/dashboard/stats', true);
            console.log('Dashboard data:', data);

            // Obtener también la lista de facturas pendientes para ver si hay múltiples deudas
            try {
                const pendingRes = await get('/api/client/invoices/pending/list', true);
                if (Array.isArray(pendingRes)) {
                    data.pendingInvoicesCount = pendingRes.length;
                    if (pendingRes.length === 1) {
                        data.singlePendingInvoiceId = pendingRes[0].id;
                    }
                }
            } catch (pendingErr) {
                console.error('Error fetching pending invoices count:', pendingErr);
            }

            setDashboardData(data);

            // También obtener datos de tráfico actuales usando la misma API que usa la página de tráfico
            try {
                const trafficResponse = await get('/api/client/traffic/current', true);
                console.log('Traffic data:', trafficResponse);
                setTrafficData(trafficResponse);
            } catch (trafficError) {
                console.error('Error obteniendo datos de tráfico:', trafficError);
                // No establecer error general, solo para tráfico
            }
        } catch (error) {
            console.error('Error al obtener datos del dashboard:', error);
            setError(error.message || 'Ocurrió un error al cargar los datos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (client) {
            fetchDashboardData();
            setPingResult(null);
        }
    }, [client]);

    if (loading) {
        return (
            <FiberLoader text="Sincronizando servicio de fibra óptica..." subtext="Cargando tu panel principal y velocidad del enlace..." fullPage />
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 4 }}>
                {error}
            </Alert>
        );
    }

    // Si no hay datos
    if (!dashboardData) {
        return (
            <Alert severity="info" sx={{ m: 4 }}>
                No se pudieron cargar los datos. Intenta recargar la página.
            </Alert>
        );
    }

    // Calcular datos a mostrar para tráfico
    const trafficDisplay = trafficData ? {
        downloadRate: trafficData.current?.download || '0',
        uploadRate: trafficData.current?.upload || '0',
        downloadBytes: trafficData.total?.download || 0,
        uploadBytes: trafficData.total?.upload || 0,
        // Calcular porcentajes para las barras de progreso (asumiendo un máximo)
        downloadPercentage: Math.min(100, ((trafficData.total?.download || 0) / (50 * 1024 * 1024 * 1024)) * 100), // 50GB como ejemplo
        uploadPercentage: Math.min(100, ((trafficData.total?.upload || 0) / (10 * 1024 * 1024 * 1024)) * 100), // 10GB como ejemplo
    } : null;

    return (
        <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 5 }, overflowX: 'hidden', maxWidth: '100%' }}>
            <Box sx={{ mb: 4, display: { xs: 'none', md: 'block' } }}>
                <Typography 
                    variant="h4" 
                    component="h1" 
                    gutterBottom 
                    sx={{ 
                        fontWeight: 'bold', 
                        fontFamily: '"Outfit", "Inter", sans-serif',
                        fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' }
                    }}
                >
                    Bienvenido, {client.fullName}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    Resumen de tu servicio de internet
                </Typography>
            </Box>

            {/* Carrusel Deslizable para Móviles (Swipeable Cards) */}
            {services && services.length > 0 && (
                <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 4, width: '100%', overflow: 'hidden' }}>
                    <Typography 
                        variant="subtitle1" 
                        fontWeight="bold" 
                        gutterBottom 
                        sx={{ 
                            color: 'text.secondary', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            fontFamily: '"Outfit", "Inter", sans-serif',
                            px: 2
                        }}
                    >
                        <WiFiIcon color="primary" fontSize="small" />
                        {services.length > 1 ? `Tus Servicios de Internet (${services.length})` : 'Mi Servicio de Internet'}
                    </Typography>
                    
                    {/* Contenedor del Slide Táctil */}
                    <Box
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 1.5,
                            px: 1,
                            width: '100%',
                            minHeight: 200
                        }}
                    >
                        {services.map((srv, idx) => {
                            const isCurrent = srv.id === client?.id;
                            const isSwitching = switchingId === srv.id;
                            const isActiveSlide = idx === activeCardIndex;
                            
                            // Renderizar únicamente la tarjeta que está activa en el carrusel
                            if (!isActiveSlide) return null;

                            return (
                                <Paper
                                    key={srv.id}
                                    className="service-carousel-card"
                                    elevation={isCurrent ? 4 : 1}
                                    sx={{
                                        width: '100%',
                                        maxWidth: 340,
                                        p: 2.5,
                                        borderRadius: 4,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        transition: 'all 0.3s ease',
                                        border: '1px solid',
                                        borderColor: isCurrent ? 'primary.light' : 'divider',
                                        background: isDarkMode
                                            ? (isCurrent 
                                                ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
                                                : 'linear-gradient(135deg, #1e293b 0%, #111827 100%)')
                                            : (isCurrent 
                                                ? 'linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)'
                                                : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'),
                                        boxShadow: isCurrent 
                                            ? '0 10px 25px -5px rgba(21, 101, 192, 0.15), 0 8px 10px -6px rgba(21, 101, 192, 0.1)'
                                            : '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                                    }}
                                >
                                    {/* Indicador de Línea Seleccionada en la esquina */}
                                    {isCurrent && (
                                        <Box 
                                            sx={{ 
                                                position: 'absolute', 
                                                top: 0, 
                                                right: 0, 
                                                bgcolor: 'primary.main', 
                                                color: 'white', 
                                                px: 2, 
                                                py: 0.5, 
                                                borderBottomLeftRadius: 12,
                                                fontSize: '0.7rem',
                                                fontWeight: 'bold',
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.5
                                            }}
                                        >
                                            Activa
                                        </Box>
                                    )}

                                    {/* Cabecera del card */}
                                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                                        <Box 
                                            sx={{ 
                                                width: 42, 
                                                height: 42, 
                                                borderRadius: 3, 
                                                bgcolor: isCurrent ? 'primary.main' : 'grey.200', 
                                                color: isCurrent ? 'white' : 'grey.600',
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                boxShadow: isCurrent ? '0 4px 10px rgba(21,101,192,0.3)' : 'none'
                                            }}
                                        >
                                            <WiFiIcon fontSize="small" />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ fontFamily: '"Outfit", "Inter", sans-serif', flexGrow: 1 }}>
                                                    {srv.alias || srv.address}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenEditAliasDialog(srv);
                                                    }}
                                                    sx={{ 
                                                        p: 0.5, 
                                                        color: 'primary.main',
                                                        bgcolor: isCurrent ? 'rgba(21, 101, 192, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                                                        '&:hover': { bgcolor: 'rgba(21, 101, 192, 0.15)' }
                                                    }}
                                                >
                                                    <EditIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Box>
                                            {srv.alias && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', noWrap: true, textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                    {srv.address}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                IP: {srv.ipAddress}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Divider sx={{ my: 1.5, borderColor: isCurrent ? 'rgba(21,101,192,0.1)' : 'divider' }} />

                                    {/* Detalles técnicos */}
                                    <Stack spacing={1} sx={{ mb: 2.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">Plan:</Typography>
                                            <Typography variant="body2" fontWeight="medium">{srv.plan}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">Estado de Línea:</Typography>
                                            <Chip 
                                                label={srv.status === 'active' ? 'Conectado' : 'Suspendido'} 
                                                size="small"
                                                color={srv.status === 'active' ? 'success' : 'error'}
                                                sx={{ height: 20, fontSize: '0.75rem', fontWeight: 'bold' }}
                                            />
                                        </Box>
                                    </Stack>

                                    {/* Botón de acción como indicador de estado */}
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        disabled={!isCurrent || isSwitching}
                                        size="small"
                                        sx={{ 
                                            py: 1, 
                                            borderRadius: 2.5,
                                            bgcolor: isCurrent ? 'rgba(21, 101, 192, 0.08) !important' : 'rgba(0,0,0,0.03) !important',
                                            color: isCurrent ? '#1565c0 !important' : 'text.disabled !important',
                                            fontWeight: 'bold',
                                            textTransform: 'none'
                                        }}
                                    >
                                        {isSwitching ? (
                                            <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                                        ) : isCurrent ? (
                                            'Gestionando Actualmente'
                                        ) : (
                                            'Cargando Servicio...'
                                        )}
                                    </Button>
                                </Paper>
                            );
                        })}
                    </Box>

                    {/* Indicadores de página (Puntos de carrusel) y botones de navegación rápida */}
                    {services.length > 1 && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mt: 0.5 }}>
                                <Button 
                                    size="small" 
                                    onClick={handlePrevSlide}
                                    disabled={activeCardIndex === 0}
                                    sx={{ 
                                        minWidth: 32, 
                                        width: 32,
                                        height: 32, 
                                        p: 0, 
                                        borderRadius: '50%',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        color: 'text.primary',
                                        '&:disabled': {
                                            color: 'text.disabled',
                                            borderColor: 'action.disabledBackground'
                                        }
                                    }}
                                >
                                    <PrevIcon fontSize="small" />
                                </Button>
                                
                                {/* Puntos / Dots */}
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    {services.map((_, idx) => (
                                        <Box
                                            key={idx}
                                            onClick={() => scrollToSlide(idx)}
                                            sx={{
                                                width: idx === activeCardIndex ? 18 : 8,
                                                height: 8,
                                                borderRadius: 4,
                                                bgcolor: idx === activeCardIndex ? 'primary.main' : 'grey.300',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                cursor: 'pointer',
                                                boxShadow: idx === activeCardIndex ? '0 1px 3px rgba(21,101,192,0.4)' : 'none',
                                                '&:hover': {
                                                    bgcolor: 'primary.light'
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>

                                <Button 
                                    size="small" 
                                    onClick={handleNextSlide}
                                    disabled={activeCardIndex === services.length - 1}
                                    sx={{ 
                                        minWidth: 32, 
                                        width: 32,
                                        height: 32, 
                                        p: 0, 
                                        borderRadius: '50%',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        color: 'text.primary',
                                        '&:disabled': {
                                            color: 'text.disabled',
                                            borderColor: 'action.disabledBackground'
                                        }
                                    }}
                                >
                                    <NextIcon fontSize="small" />
                                </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5, fontStyle: 'italic' }}>
                                Desliza sobre la tarjeta o usa las flechas para ver otras líneas
                            </Typography>
                        </>
                    )}
                </Box>
            )}

            {/* Banner de instalación personalizada PWA */}
            <PWAInstallBanner installPrompt={deferredPrompt} />

            {/* Alertas de facturación y ciclo de pago */}
            <BillingAlerts
                billingCycle={dashboardData.billingCycle}
                lastInvoice={dashboardData.lastInvoice}
                clientStatus={dashboardData.client.status}
                onRefresh={fetchDashboardData}
            />

            <Grid container spacing={{ xs: 2, md: 3 }} sx={{ width: '100%', m: 0, mb: 4 }}>
                {/* 1. Facturación / Última Factura */}
                <Grid item xs={12} md={4}>
                    <Paper 
                        elevation={3} 
                        sx={{ 
                            p: { xs: 2, md: 3 }, 
                            height: '100%',
                            borderRadius: 4,
                            background: isDarkMode
                                ? (dashboardData.lastInvoice && dashboardData.lastInvoice.status !== 'paid'
                                    ? 'linear-gradient(135deg, #1e293b 0%, #4c1d1d 100%)'
                                    : 'linear-gradient(135deg, #1e293b 0%, #14532d 100%)')
                                : (dashboardData.lastInvoice && dashboardData.lastInvoice.status !== 'paid'
                                    ? 'linear-gradient(135deg, #ffffff 0%, #fff5f5 100%)'
                                    : 'linear-gradient(135deg, #ffffff 0%, #f6fff6 100%)'),
                            border: '1px solid',
                            borderColor: dashboardData.lastInvoice && dashboardData.lastInvoice.status !== 'paid'
                                ? 'error.light'
                                : 'success.light',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                        }}
                    >
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, md: 1.5 } }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', fontFamily: '"Outfit", "Inter", sans-serif', fontSize: { xs: '1.05rem', md: '1.25rem' } }}>
                                    <ReceiptIcon sx={{ mr: 1, color: dashboardData.lastInvoice && dashboardData.lastInvoice.status !== 'paid' ? 'error.main' : 'success.main' }} />
                                    Facturación
                                </Typography>
                                <Chip 
                                    label={dashboardData.lastInvoice ? (dashboardData.lastInvoice.status === 'paid' ? 'Al Día' : 'Pago Pendiente') : 'Sin Deudas'}
                                    color={dashboardData.lastInvoice ? (dashboardData.lastInvoice.status === 'paid' ? 'success' : 'error') : 'success'}
                                    size="small"
                                    sx={{ fontWeight: 'bold', height: 24, fontSize: '0.75rem' }}
                                />
                            </Box>
                            <Divider sx={{ mb: { xs: 1, md: 1.5 } }} />

                            {dashboardData.lastInvoice ? (
                                <Box>
                                    <Grid container spacing={1} sx={{ mb: 1 }}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="textSecondary" display="block">Recibo</Typography>
                                            <Typography variant="body2" fontWeight="bold">#{dashboardData.lastInvoice.invoice_number}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="textSecondary" display="block">Total</Typography>
                                            <Typography variant="body2" fontWeight="bold" color={dashboardData.lastInvoice.status !== 'paid' ? 'error.main' : 'success.main'}>
                                                S/ {parseFloat(dashboardData.lastInvoice.amount).toFixed(2)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} sx={{ mt: 1 }}>
                                            <Typography variant="caption" color="textSecondary" display="block">Emisión</Typography>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{formatDate(dashboardData.lastInvoice.created_at || dashboardData.lastInvoice.createdAt).split(' de ')[0] + ' ' + formatDate(dashboardData.lastInvoice.created_at || dashboardData.lastInvoice.createdAt).split(' de ')[1]?.substring(0,3)}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sx={{ mt: 1 }}>
                                            <Typography variant="caption" color="textSecondary" display="block">Vence</Typography>
                                            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }} color={parseLocalDate(dashboardData.lastInvoice.payment_due_date || dashboardData.lastInvoice.paymentDueDate || dashboardData.lastInvoice.due_date) < new Date() && dashboardData.lastInvoice.status !== 'paid' ? 'error.main' : 'text.primary'}>
                                                {formatDate(dashboardData.lastInvoice.payment_due_date || dashboardData.lastInvoice.paymentDueDate || dashboardData.lastInvoice.due_date).split(' de ')[0] + ' ' + formatDate(dashboardData.lastInvoice.payment_due_date || dashboardData.lastInvoice.paymentDueDate || dashboardData.lastInvoice.due_date).split(' de ')[1]?.substring(0,3)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Box sx={{ py: 1.5, textAlign: 'center' }}>
                                    <CheckIcon color="success" sx={{ fontSize: 32, mb: 0.5 }} />
                                    <Typography variant="body2" fontWeight="medium">¡Felicidades! No tienes facturas pendientes.</Typography>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ mt: 1 }}>
                            {dashboardData.lastInvoice && dashboardData.lastInvoice.status !== 'paid' ? (
                                dashboardData.lastInvoice.has_pending_payment ? (
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        fullWidth
                                        disabled
                                        startIcon={<WarningIcon />}
                                        sx={{ borderRadius: 2.5, py: 0.75, textTransform: 'none', fontWeight: 'bold', fontSize: '0.8rem' }}
                                    >
                                        Pago en verificación
                                    </Button>
                                ) : (
                                    <Button
                                        component={Link}
                                        to="/client/payments"
                                        state={{ invoiceId: dashboardData.lastInvoice?.id }}
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        sx={{ 
                                            borderRadius: 2.5, 
                                            py: 0.85, 
                                            textTransform: 'none', 
                                            fontWeight: 'bold',
                                            fontSize: '0.85rem',
                                            boxShadow: '0 4px 12px rgba(21, 101, 192, 0.25)'
                                        }}
                                    >
                                        Ir a Pagos
                                    </Button>
                                )
                            ) : (
                                <Button
                                    component={Link}
                                    to="/client/payments"
                                    variant="outlined"
                                    color="success"
                                    fullWidth
                                    sx={{ borderRadius: 2.5, py: 0.75, textTransform: 'none', fontWeight: 'bold', fontSize: '0.8rem' }}
                                >
                                    Ver Historial de Pagos
                                </Button>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* 1.5. Saldo a Favor / Crédito */}
                <Grid item xs={12} md={4}>
                    <Paper 
                        elevation={3} 
                        sx={{ 
                            p: { xs: 2, md: 3 }, 
                            height: '100%', 
                            borderRadius: 4,
                            background: isDarkMode
                                ? (parseFloat(dashboardData.client.balance || 0) > 0
                                    ? 'linear-gradient(135deg, #1e293b 0%, #064e3b 100%)'
                                    : 'linear-gradient(135deg, #1e293b 0%, #1e293b 100%)')
                                : (parseFloat(dashboardData.client.balance || 0) > 0
                                    ? 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)'
                                    : 'linear-gradient(135deg, #ffffff 0%, #fbfbfb 100%)'),
                            border: '1px solid',
                            borderColor: parseFloat(dashboardData.client.balance || 0) > 0
                                ? 'success.light'
                                : 'divider',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                        }}
                    >
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, md: 1.5 } }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', fontFamily: '"Outfit", "Inter", sans-serif', fontSize: { xs: '1.05rem', md: '1.25rem' } }}>
                                    <MonetizationOnIcon sx={{ mr: 1, color: parseFloat(dashboardData.client.balance || 0) > 0 ? 'success.main' : 'text.secondary' }} />
                                    Saldo a Favor
                                </Typography>
                                {parseFloat(dashboardData.client.balance || 0) > 0 && (
                                    <Chip 
                                        label="Con Crédito"
                                        color="success"
                                        size="small"
                                        sx={{ fontWeight: 'bold', height: 24, fontSize: '0.75rem' }}
                                    />
                                )}
                            </Box>
                            <Divider sx={{ mb: { xs: 1, md: 1.5 } }} />

                            <Box sx={{ py: 1, textAlign: 'center' }}>
                                <Typography variant="caption" color="textSecondary" display="block">Saldo Disponible</Typography>
                                <Typography variant="h4" fontWeight="800" color={parseFloat(dashboardData.client.balance || 0) > 0 ? 'success.main' : 'text.primary'} sx={{ my: 1, fontFamily: '"Outfit", "Inter", sans-serif' }}>
                                    S/ {parseFloat(dashboardData.client.balance || 0).toFixed(2)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', px: 1, fontSize: '0.75rem', lineHeight: 1.3 }}>
                                    {parseFloat(dashboardData.client.balance || 0) > 0 
                                        ? "Este saldo se aplicará automáticamente para pagar su próximo recibo mensual en la fecha de facturación."
                                        : "No tienes crédito acumulado. Puedes realizar abonos por adelantado en cualquier momento para pagar tu servicio."
                                    }
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ mt: 1 }}>
                            <Button
                                component={Link}
                                to="/client/payments"
                                state={{ isAdvance: true }}
                                variant="contained"
                                color={parseFloat(dashboardData.client.balance || 0) > 0 ? 'success' : 'primary'}
                                fullWidth
                                sx={{ 
                                    borderRadius: 2.5, 
                                    py: 0.85, 
                                    textTransform: 'none', 
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    boxShadow: parseFloat(dashboardData.client.balance || 0) > 0 ? '0 4px 12px rgba(16, 185, 129, 0.2)' : 'none',
                                    bgcolor: parseFloat(dashboardData.client.balance || 0) > 0 ? 'success.main' : 'primary.main',
                                    '&:hover': {
                                        bgcolor: parseFloat(dashboardData.client.balance || 0) > 0 ? 'success.dark' : 'primary.dark'
                                    }
                                }}
                            >
                                {parseFloat(dashboardData.client.balance || 0) > 0 ? 'Ver Mis Pagos' : 'Abonar por Adelantado'}
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* 2. Ciclo de Facturación */}
                <Grid item xs={12} md={4}>
                    <Paper 
                        elevation={3} 
                        sx={{ 
                            p: { xs: 2, md: 3 }, 
                            height: '100%', 
                            borderRadius: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            background: isDarkMode ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' : 'linear-gradient(135deg, #ffffff 0%, #fcfdfe 100%)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                        }}
                    >
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, md: 1.5 } }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', fontFamily: '"Outfit", "Inter", sans-serif', fontSize: { xs: '1.05rem', md: '1.25rem' } }}>
                                    <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    Ciclo de Facturación
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: { xs: 1, md: 1.5 } }} />

                            {dashboardData.billingCycle ? (
                                <Grid container spacing={1} sx={{ mt: 0 }}>
                                    <Grid item xs={4} sx={{ textAlign: 'center' }}>
                                        <Box sx={{ py: 0.75, px: 0.5, bgcolor: isDarkMode ? 'rgba(21, 101, 192, 0.15)' : '#e3f2fd', borderRadius: 2 }}>
                                            <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.65rem', fontWeight: 'medium' }}>Emisión</Typography>
                                            <Typography variant="body2" fontWeight="bold" color="primary.main" sx={{ fontSize: '0.85rem' }}>
                                                {dashboardData.billingCycle.billingDay ? `Día ${dashboardData.billingCycle.billingDay}` : '-'}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4} sx={{ textAlign: 'center' }}>
                                        <Box sx={{ py: 0.75, px: 0.5, bgcolor: isDarkMode ? 'rgba(239, 108, 0, 0.15)' : '#fff3e0', borderRadius: 2 }}>
                                            <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.65rem', fontWeight: 'medium' }}>Vence</Typography>
                                            <Typography variant="body2" fontWeight="bold" color="warning.dark" sx={{ fontSize: '0.85rem' }}>
                                                {dashboardData.billingCycle.paymentDay ? `Día ${dashboardData.billingCycle.paymentDay}` : '-'}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4} sx={{ textAlign: 'center' }}>
                                        <Box sx={{ py: 0.75, px: 0.5, bgcolor: isDarkMode ? 'rgba(211, 47, 47, 0.15)' : '#ffebee', borderRadius: 2 }}>
                                            <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.65rem', fontWeight: 'medium' }}>Corte</Typography>
                                            <Typography variant="body2" fontWeight="bold" color="error.main" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {dashboardData.billingCycle.cutoffDate ? 
                                                    parseLocalDate(dashboardData.billingCycle.cutoffDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : 
                                                    '-'}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Typography variant="body2" sx={{ textAlign: 'center', py: 1 }}>
                                    No hay ciclo configurado
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ mt: 1.5 }}>
                            {dashboardData.billingCycle && dashboardData.billingCycle.daysUntilPayment !== null && (
                                <Box 
                                    sx={{ 
                                        py: 0.75, 
                                        px: 1, 
                                        bgcolor: dashboardData.billingCycle.daysUntilPayment > 0 ? (isDarkMode ? 'rgba(21, 101, 192, 0.15)' : 'rgba(21, 101, 192, 0.04)') : (isDarkMode ? 'rgba(211, 47, 47, 0.15)' : '#ffebee'), 
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: dashboardData.billingCycle.daysUntilPayment > 0 ? 'rgba(21, 101, 192, 0.2)' : 'error.light'
                                    }}
                                >
                                    <Typography variant="caption" align="center" display="block" fontWeight="medium" color={dashboardData.billingCycle.daysUntilPayment > 0 ? 'text.primary' : 'error.main'} sx={{ fontSize: '0.75rem' }}>
                                        {dashboardData.billingCycle.daysUntilPayment > 0 ? (
                                            <>
                                                Faltan <strong>{dashboardData.billingCycle.daysUntilPayment} días</strong> para tu vencimiento.
                                            </>
                                        ) : (
                                            <strong>¡Tu pago está vencido y tu servicio podría suspenderse!</strong>
                                        )}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* Fila de Tarjetas de Estado (Simétricas, de igual altura y compactas) */}
                {/* 3. Estado del Servicio */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper 
                        elevation={2} 
                        sx={{ 
                            p: { xs: 2, md: 3 }, 
                            height: '100%', 
                            bgcolor: dashboardData.client.status === 'active' ? (isDarkMode ? 'rgba(76, 175, 80, 0.15)' : '#e8f5e9') : (isDarkMode ? 'rgba(211, 47, 47, 0.15)' : '#fbe9e7'),
                            border: '1px solid',
                            borderColor: dashboardData.client.status === 'active' ? 'success.light' : 'error.light',
                            borderRadius: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                        }}
                    >
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography color="text.secondary" sx={{ fontSize: '0.85rem', fontWeight: 'medium' }}>
                                    Estado del Servicio
                                </Typography>
                                <Box sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: '#ffffff'
                                }}>
                                    <CheckIcon sx={{ color: dashboardData.client.status === 'active' ? 'success.main' : 'error.main' }} fontSize="small" />
                                </Box>
                            </Box>
                            <Typography 
                                variant="h6" 
                                component="div" 
                                sx={{ 
                                    fontWeight: 'bold', 
                                    color: dashboardData.client.status === 'active' ? 'success.dark' : 'error.dark',
                                    fontSize: { xs: '1.15rem', md: '1.25rem' },
                                    mb: 1
                                }}
                            >
                                {dashboardData.client.status === 'active' ? 'Activo' : 'Suspendido'}
                            </Typography>
                        </Box>

                        <Box sx={{ mt: 2, width: '100%' }}>
                            {pingLoading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                                    <CircularProgress size={14} />
                                    <Typography variant="caption" color="textSecondary">Probando enlace...</Typography>
                                </Box>
                            ) : pingResult ? (
                                <Box sx={{ mb: 1 }}>
                                    <Typography 
                                        variant="caption" 
                                        fontWeight="bold" 
                                        sx={{ 
                                            color: pingResult.online ? 'success.dark' : 'error.main',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {pingResult.online ? '✓ Enlace Activo' : '✗ Sin Conexión'}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.65rem', mt: 0.2 }}>
                                        {pingResult.message}
                                    </Typography>
                                </Box>
                            ) : null}
                            <Button
                                variant="contained"
                                size="small"
                                fullWidth
                                disabled={pingLoading}
                                onClick={handlePingTest}
                                sx={{ 
                                    textTransform: 'none', 
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                    py: 0.6,
                                    borderRadius: 2,
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Probar mi Enlace
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* 5. Plan Contratado */}
                <Grid item xs={12} sm={6} md={3}>
                    <StatusCard
                        title="Plan Contratado"
                        value={dashboardData.client.plan_name}
                        icon={SpeedIcon}
                        color="primary"
                    />
                </Grid>

                {/* 6. Velocidad Asignada */}
                <Grid item xs={12} sm={6} md={3}>
                    <StatusCard
                        title="Velocidad Asignada"
                        value={`${dashboardData.client.download_speed}/${dashboardData.client.upload_speed} Mbps`}
                        icon={NetworkIcon}
                        color="info"
                    />
                </Grid>

                {/* 7. Tickets Abiertos */}
                <Grid item xs={12} sm={6} md={3}>
                    <StatusCard
                        title="Soporte (Tickets)"
                        value={dashboardData.openTickets === 0 ? "Sin incidentes" : `${dashboardData.openTickets} Activos`}
                        icon={SupportIcon}
                        color={dashboardData.openTickets === 0 ? "success" : "warning"}
                    />
                </Grid>

                {/* 4. Uso de Internet */}
                <Grid item xs={12} md={8}>
                    <Paper elevation={3} sx={{ p: 3, height: '100%', borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', fontFamily: '"Outfit", "Inter", sans-serif' }}>
                                <NetworkIcon sx={{ mr: 1, color: 'info.main' }} />
                                Uso de Internet
                            </Typography>
                            <Button
                                component={Link}
                                to="/client/traffic"
                                variant="outlined"
                                size="small"
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                                Ver Detalles
                            </Button>
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        {trafficDisplay ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary">Tasa Descarga</Typography>
                                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', color: 'success.main' }}>
                                            ▼ {formatRate(trafficDisplay.downloadRate)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary">Tasa Subida</Typography>
                                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', color: 'primary.main' }}>
                                            ▲ {formatRate(trafficDisplay.uploadRate)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                
                                <Box sx={{ mt: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="caption" color="textSecondary">Total Descargado:</Typography>
                                        <Typography variant="caption" fontWeight="bold">{formatBytes(trafficDisplay.downloadBytes)}</Typography>
                                    </Box>
                                    <Box sx={{ width: '100%', bgcolor: 'grey.100', borderRadius: 2, height: 8, overflow: 'hidden' }}>
                                        <Box
                                            sx={{
                                                bgcolor: 'success.main',
                                                height: '100%',
                                                width: `${trafficDisplay.downloadPercentage}%`,
                                                transition: 'width 0.5s ease-out'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="caption" color="textSecondary">Total Subido:</Typography>
                                        <Typography variant="caption" fontWeight="bold">{formatBytes(trafficDisplay.uploadBytes)}</Typography>
                                    </Box>
                                    <Box sx={{ width: '100%', bgcolor: 'grey.100', borderRadius: 2, height: 8, overflow: 'hidden' }}>
                                        <Box
                                            sx={{
                                                bgcolor: 'primary.main',
                                                height: '100%',
                                                width: `${trafficDisplay.uploadPercentage}%`,
                                                transition: 'width 0.5s ease-out'
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Typography variant="body2" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                                No hay datos de tráfico en tiempo real disponibles.
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* 4.5. Servicios Adicionales */}
                <Grid item xs={12} md={4}>
                    <Paper 
                        elevation={3} 
                        sx={{ 
                            p: 3, 
                            height: '100%', 
                            borderRadius: 4, 
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            justifyContent: 'space-between' 
                        }}
                    >
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', fontFamily: '"Outfit", "Inter", sans-serif' }}>
                                    <StreamingIcon sx={{ mr: 1, color: 'warning.main' }} />
                                    Servicios Adicionales
                                </Typography>
                                {dashboardData.additionalServices && dashboardData.additionalServices.length > 0 && (
                                    <Chip 
                                        label={`${dashboardData.additionalServices.length} Activo(s)`} 
                                        color="primary" 
                                        size="small" 
                                        sx={{ fontWeight: 'bold', height: 24, fontSize: '0.75rem' }} 
                                    />
                                )}
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {dashboardData.additionalServices && dashboardData.additionalServices.length > 0 ? (
                                <Stack spacing={1.5}>
                                    {dashboardData.additionalServices.map((service) => {
                                        const isPromoDiscount = parseFloat(dashboardData.client.discount_amount || 0) > 0 && 
                                                                parseInt(dashboardData.client.discount_months || 0) > 0;
                                        const isFree = service.price === 0 || 
                                                       (isPromoDiscount && parseFloat(dashboardData.client.discount_amount) >= service.service_real_price);

                                        return (
                                            <Box 
                                                key={service.id} 
                                                sx={{ 
                                                    p: 1.5, 
                                                    bgcolor: '#f8fafc', 
                                                    borderRadius: 3, 
                                                    border: '1px solid #f1f5f9',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: isFree ? 'rgba(16, 185, 129, 0.1)' : 'rgba(21, 101, 192, 0.1)', color: isFree ? '#10b981' : 'primary.main', display: 'flex' }}>
                                                        {service.service_name.toLowerCase().includes('velocidad') ? <SpeedIcon fontSize="small" /> : <StreamingIcon fontSize="small" />}
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', lineHeight: 1.2 }}>
                                                            {service.service_name}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.2 }}>
                                                            {service.service_description || 'Servicio activo.'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    {isFree ? (
                                                        <Chip 
                                                            label="GRATIS" 
                                                            color="success" 
                                                            size="small" 
                                                            sx={{ fontWeight: 'bold', height: 20, fontSize: '0.65rem' }} 
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" fontWeight="bold" color="text.primary">
                                                            S/ {service.price.toFixed(2)}/mes
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            ) : (
                                <Box sx={{ py: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <StreamingIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1.5 }} />
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1, px: 2 }}>
                                        No tienes servicios de streaming o adicionales activos actualmente.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        
                        <Box sx={{ mt: 2 }}>
                            {(!dashboardData.additionalServices || dashboardData.additionalServices.length === 0) ? (
                                <Button
                                    component={Link}
                                    to="/client/promotions"
                                    variant="contained"
                                    color="warning"
                                    fullWidth
                                    startIcon={<GiftIcon />}
                                    sx={{ 
                                        borderRadius: 2.5, 
                                        py: 1, 
                                        textTransform: 'none', 
                                        fontWeight: 'bold', 
                                        fontSize: '0.85rem',
                                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                                            boxShadow: 'none'
                                        }
                                    }}
                                >
                                    Explorar Promociones
                                </Button>
                            ) : (
                                <Button
                                    component={Link}
                                    to="/client/promotions"
                                    variant="outlined"
                                    color="primary"
                                    fullWidth
                                    startIcon={<GiftIcon />}
                                    sx={{ borderRadius: 2.5, py: 0.85, textTransform: 'none', fontWeight: 'bold', fontSize: '0.8rem' }}
                                >
                                    Ver Más Ofertas
                                </Button>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Diálogo para Editar Nombre de Servicio (Alias) */}
            <Dialog 
                open={editAliasOpen} 
                onClose={handleCloseEditAliasDialog}
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        p: 1.5,
                        minWidth: { xs: 280, sm: 380 },
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontFamily: '"Outfit", "Inter", sans-serif', fontSize: '1.25rem', pb: 1 }}>
                    Personalizar Servicio
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
                        Asigna un nombre fácil de recordar a tu servicio de internet contratado en la dirección:
                        <strong style={{ display: 'block', marginTop: '6px', color: '#1565c0', fontSize: '0.9rem' }}>{selectedServiceForAlias?.address}</strong>
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nombre del Servicio (ej. Casa, Oficina, Local)"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newAliasValue}
                        onChange={(e) => setNewAliasValue(e.target.value)}
                        placeholder="Ej. Mi Casa"
                        inputProps={{ maxLength: 30 }}
                        sx={{
                            mt: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button 
                        onClick={handleCloseEditAliasDialog} 
                        color="inherit"
                        sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '0.85rem' }}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleSaveAlias} 
                        variant="contained"
                        disabled={savingAlias}
                        sx={{ 
                            textTransform: 'none', 
                            fontWeight: 'bold', 
                            borderRadius: 2.5,
                            fontSize: '0.85rem',
                            px: 3,
                            bgcolor: '#1565c0',
                            '&:hover': { bgcolor: '#0d47a1' },
                            boxShadow: '0 4px 12px rgba(21, 101, 192, 0.25)'
                        }}
                    >
                        {savingAlias ? 'Guardando...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ClientDashboard;